import { cityById } from "./cities.ts";
import { LAKH } from "./types.ts";

/**
 * The expense planner builds the one number the calculator is most sensitive
 * to — annual lifestyle spend — out of the line items people actually pay.
 *
 * Standards are for a Bengaluru household of two adults and two school-age
 * children, in today's rupees, at three levels of comfort. Each line then moves
 * with the city by its own elasticity (a cook's wage tracks local costs; an
 * international flight does not) and with the household by its own scaling
 * rule (groceries grow with every mouth; society maintenance does not).
 *
 * Deliberately excluded, because the calculator models them separately with
 * their own inflation rates: school fees, university, health insurance,
 * parents' care, travel to reach healthcare, and tax. Counting them here too
 * would double-count them.
 */

export type Tier = "comfortable" | "fat" | "ultra";
export type Frequency = "monthly" | "yearly" | "every-n-years";

/** How a line grows with the household. */
export type Scaling =
  | "fixed" // same for any household: maintenance, internet, the car
  | "household" // every person eats and wears clothes
  | "travellers"; // who actually goes on the trip

export interface StandardItem {
  id: string;
  group: ExpenseGroup;
  label: string;
  frequency: Frequency;
  /** For every-n-years items: how often the cost recurs. */
  everyYears?: number;
  /** Amount per period (per month, per year, or per occurrence), Bengaluru, 2+2 household. */
  standard: Record<Tier, number>;
  /**
   * How far this line follows the city cost index. 1 = fully local (wages,
   * maintenance), 0 = priced the same everywhere (flights, cars, gadgets).
   */
  elasticity: number;
  scaling: Scaling;
  note: string;
}

export type ExpenseGroup =
  | "Home"
  | "Utilities"
  | "Household help"
  | "Food"
  | "Transport"
  | "Travel"
  | "Personal"
  | "Family & giving";

export const GROUPS: ExpenseGroup[] = [
  "Home", "Utilities", "Household help", "Food", "Transport", "Travel", "Personal", "Family & giving",
];

const K = 1000;

export const STANDARD_ITEMS: StandardItem[] = [
  // ------------------------------------------------------------------ Home
  { id: "maintenance", group: "Home", label: "Society maintenance & property tax", frequency: "monthly",
    standard: { comfortable: 8 * K, fat: 15 * K, ultra: 30 * K }, elasticity: 1, scaling: "fixed",
    note: "Gated-community charges scale with flat size and amenities. Assumes the home is owned — rent is not a lifestyle cost in this plan, it is a housing decision." },
  { id: "repairs", group: "Home", label: "Repairs, painting & upkeep", frequency: "yearly",
    standard: { comfortable: 1.5 * LAKH, fat: 3 * LAKH, ultra: 6 * LAKH }, elasticity: 0.8, scaling: "fixed",
    note: "Roughly 1% of a home's value a year, averaged over the big jobs." },
  { id: "furnishing", group: "Home", label: "Appliances & furniture replacement", frequency: "yearly",
    standard: { comfortable: 1 * LAKH, fat: 2 * LAKH, ultra: 4 * LAKH }, elasticity: 0.2, scaling: "fixed",
    note: "A sinking fund: ACs, fridge, washing machine and sofas all die eventually." },

  // ------------------------------------------------------------- Utilities
  { id: "power", group: "Utilities", label: "Electricity & water", frequency: "monthly",
    standard: { comfortable: 5 * K, fat: 8 * K, ultra: 15 * K }, elasticity: 0.5, scaling: "fixed",
    note: "Air-conditioning is the swing factor. Add a generator or inverter if the town needs one." },
  { id: "gas", group: "Utilities", label: "Cooking gas", frequency: "monthly",
    standard: { comfortable: 1.2 * K, fat: 1.5 * K, ultra: 2 * K }, elasticity: 0.3, scaling: "household",
    note: "Piped gas or LPG cylinders." },
  { id: "connectivity", group: "Utilities", label: "Internet, mobiles & streaming", frequency: "monthly",
    standard: { comfortable: 3 * K, fat: 5 * K, ultra: 8 * K }, elasticity: 0.1, scaling: "fixed",
    note: "Fibre, four mobile plans, and the streaming subscriptions that accumulate." },

  // -------------------------------------------------------- Household help
  { id: "maid", group: "Household help", label: "Cleaning help", frequency: "monthly",
    standard: { comfortable: 9 * K, fat: 22 * K, ultra: 40 * K }, elasticity: 1, scaling: "fixed",
    note: "Bengaluru 2026: part-time ₹4,500–9,000 a month, full-time or live-in ₹16,000–26,000. The top tier assumes two staff." },
  { id: "cook", group: "Household help", label: "Cook", frequency: "monthly",
    standard: { comfortable: 12 * K, fat: 18 * K, ultra: 30 * K }, elasticity: 1, scaling: "fixed",
    note: "Bengaluru 2026: ₹10,000–18,000 a month. The top tier is a full-time cook for all meals." },
  { id: "driver", group: "Household help", label: "Driver", frequency: "monthly",
    standard: { comfortable: 0, fat: 25 * K, ultra: 35 * K }, elasticity: 1, scaling: "fixed",
    note: "Estimated ₹20,000–35,000 a month full-time. Worth more in a hill town with no cabs, and more still as you age." },

  // ------------------------------------------------------------------ Food
  { id: "groceries", group: "Food", label: "Groceries & household supplies", frequency: "monthly",
    standard: { comfortable: 30 * K, fat: 38 * K, ultra: 55 * K }, elasticity: 0.6, scaling: "household",
    note: "Includes cleaning supplies and toiletries. Imported and organic produce is what moves the top tier." },
  { id: "dining", group: "Food", label: "Dining out & ordering in", frequency: "monthly",
    standard: { comfortable: 15 * K, fat: 25 * K, ultra: 40 * K }, elasticity: 0.8, scaling: "household",
    note: "Delivery apps make this the line that quietly doubles." },

  // ------------------------------------------------------------- Transport
  { id: "fuel", group: "Transport", label: "Fuel, parking, tolls & cabs", frequency: "monthly",
    standard: { comfortable: 10 * K, fat: 15 * K, ultra: 25 * K }, elasticity: 0.3, scaling: "fixed",
    note: "Two cars at the top tier." },
  { id: "car-running", group: "Transport", label: "Car insurance & servicing", frequency: "yearly",
    standard: { comfortable: 60 * K, fat: 1 * LAKH, ultra: 2 * LAKH }, elasticity: 0.2, scaling: "fixed",
    note: "Comprehensive cover plus scheduled servicing." },
  { id: "car-replace", group: "Transport", label: "Car replacement", frequency: "every-n-years", everyYears: 8,
    standard: { comfortable: 12 * LAKH, fat: 25 * LAKH, ultra: 45 * LAKH }, elasticity: 0, scaling: "fixed",
    note: "The price of the car, spread over how long you keep it. Priced the same everywhere." },

  // ---------------------------------------------------------------- Travel
  { id: "travel-domestic", group: "Travel", label: "Domestic holidays", frequency: "yearly",
    standard: { comfortable: 2.5 * LAKH, fat: 4 * LAKH, ultra: 6 * LAKH }, elasticity: 0.2, scaling: "travellers",
    note: "Two or three trips a year." },
  { id: "travel-intl", group: "Travel", label: "International holidays", frequency: "yearly",
    standard: { comfortable: 4 * LAKH, fat: 10 * LAKH, ultra: 18 * LAKH }, elasticity: 0, scaling: "travellers",
    note: "Returnees on r/FatFIREIndia report two international and two domestic trips a year with children fit in about ₹20 L — a family of four to London was ₹8.5 L all in." },

  // -------------------------------------------------------------- Personal
  { id: "clothing", group: "Personal", label: "Clothing & shoes", frequency: "yearly",
    standard: { comfortable: 1.5 * LAKH, fat: 3 * LAKH, ultra: 6 * LAKH }, elasticity: 0.2, scaling: "household",
    note: "Festival wear is under Family & giving." },
  { id: "care", group: "Personal", label: "Grooming, gym & wellness", frequency: "monthly",
    standard: { comfortable: 6 * K, fat: 10 * K, ultra: 25 * K }, elasticity: 0.7, scaling: "household",
    note: "A personal trainer on its own is ₹15,000–30,000 a month." },
  { id: "hobbies", group: "Personal", label: "Hobbies, sport & entertainment", frequency: "monthly",
    standard: { comfortable: 8 * K, fat: 15 * K, ultra: 30 * K }, elasticity: 0.6, scaling: "household",
    note: "The adults' side. Children's classes and coaching sit in the calculator's schooling line." },
  { id: "club", group: "Personal", label: "Club membership", frequency: "yearly",
    standard: { comfortable: 0, fat: 1 * LAKH, ultra: 2.5 * LAKH }, elasticity: 1, scaling: "fixed",
    note: "Annual dues. The one-time entry fee can be several lakh on top." },
  { id: "gadgets", group: "Personal", label: "Phones, laptops & electronics", frequency: "yearly",
    standard: { comfortable: 1 * LAKH, fat: 2 * LAKH, ultra: 4 * LAKH }, elasticity: 0, scaling: "household",
    note: "Priced the same everywhere." },

  // -------------------------------------------------------- Family & giving
  { id: "festivals", group: "Family & giving", label: "Festivals, gifts & weddings", frequency: "yearly",
    standard: { comfortable: 1.5 * LAKH, fat: 3 * LAKH, ultra: 6 * LAKH }, elasticity: 0.5, scaling: "fixed",
    note: "Families moving back tend to underestimate this. Proximity to relatives brings more weddings." },
  { id: "donations", group: "Family & giving", label: "Donations", frequency: "yearly",
    standard: { comfortable: 50 * K, fat: 1 * LAKH, ultra: 3 * LAKH }, elasticity: 0, scaling: "fixed",
    note: "" },
];

export interface CustomItem {
  id: string;
  label: string;
  amount: number;
  frequency: Frequency;
  everyYears: number;
}

export interface ExpensePlan {
  cityId: string;
  tier: Tier;
  adults: number;
  children: number;
  /** Parents living in the household — their food and clothes, not their care. */
  parentsLiveIn: number;
  /** User-entered amount per item, in that item's own period. Absent = use the standard. */
  overrides: Record<string, number>;
  custom: CustomItem[];
  /** Allowance for the things nobody remembers to list. */
  bufferPct: number;
}

export const TIER_LABEL: Record<Tier, string> = {
  comfortable: "Comfortable",
  fat: "Fat",
  ultra: "Ultra",
};

export const DEFAULT_PLAN: ExpensePlan = {
  cityId: "bengaluru",
  tier: "fat",
  adults: 2,
  children: 2,
  parentsLiveIn: 0,
  overrides: {},
  custom: [],
  bufferPct: 0.075,
};

/** The reference household the standards are quoted for. */
const BASE_HOUSEHOLD = 2 + 2 * 0.6; // two adults, two children at 0.6 each
const BASE_TRAVELLERS = 2 + 2 * 0.8;

export function householdFactor(plan: ExpensePlan, scaling: Scaling): number {
  if (scaling === "fixed") return 1;
  if (scaling === "travellers") {
    // Elderly parents rarely come on every trip; count them at half.
    return (plan.adults + 0.8 * plan.children + 0.5 * plan.parentsLiveIn) / BASE_TRAVELLERS;
  }
  return (plan.adults + 0.6 * plan.children + 0.8 * plan.parentsLiveIn) / BASE_HOUSEHOLD;
}

/** Per-period standard amount for this plan's city and household. */
export function standardFor(item: StandardItem, plan: ExpensePlan): number {
  const city = cityById(plan.cityId);
  const cityFactor = 1 + item.elasticity * (city.index - 1);
  const raw = item.standard[plan.tier] * cityFactor * householdFactor(plan, item.scaling);
  // Round to something a person would actually write down.
  const step = raw >= 1 * LAKH ? 5000 : raw >= 10000 ? 500 : 100;
  return Math.round(raw / step) * step;
}

export function toAnnual(amount: number, frequency: Frequency, everyYears = 1): number {
  if (!Number.isFinite(amount) || amount < 0) return 0;
  if (frequency === "monthly") return amount * 12;
  if (frequency === "every-n-years") return amount / Math.max(1, everyYears);
  return amount;
}

export interface LineResult {
  id: string;
  group: ExpenseGroup | "Your additions";
  label: string;
  frequency: Frequency;
  everyYears?: number;
  standard: number | null;
  amount: number;
  annual: number;
  /** Relative gap to the standard: 0.3 means 30% above it. Null when there is no standard. */
  deviation: number | null;
  note: string;
}

export interface ExpenseSummary {
  lines: LineResult[];
  byGroup: { group: string; annual: number }[];
  subtotal: number;
  buffer: number;
  annualTotal: number;
  monthlyTotal: number;
  /** The same plan with every line at its standard, for comparison. */
  standardTotal: number;
}

export function summarise(plan: ExpensePlan): ExpenseSummary {
  const lines: LineResult[] = [];
  let standardSubtotal = 0;

  for (const item of STANDARD_ITEMS) {
    const std = standardFor(item, plan);
    const override = plan.overrides[item.id];
    const amount = Number.isFinite(override) && override >= 0 ? override : std;
    const annual = toAnnual(amount, item.frequency, item.everyYears);
    standardSubtotal += toAnnual(std, item.frequency, item.everyYears);
    lines.push({
      id: item.id,
      group: item.group,
      label: item.label,
      frequency: item.frequency,
      everyYears: item.everyYears,
      standard: std,
      amount,
      annual,
      deviation: std > 0 ? amount / std - 1 : amount > 0 ? Infinity : 0,
      note: item.note,
    });
  }

  for (const c of plan.custom) {
    const annual = toAnnual(c.amount, c.frequency, c.everyYears);
    lines.push({
      id: c.id,
      group: "Your additions",
      label: c.label || "Untitled expense",
      frequency: c.frequency,
      everyYears: c.everyYears,
      standard: null,
      amount: c.amount,
      annual,
      deviation: null,
      note: "",
    });
  }

  const groupNames = [...GROUPS, "Your additions"];
  const byGroup = groupNames
    .map((group) => ({
      group,
      annual: lines.filter((l) => l.group === group).reduce((a, l) => a + l.annual, 0),
    }))
    .filter((g) => g.annual > 0);

  const subtotal = lines.reduce((a, l) => a + l.annual, 0);
  const bufferPct = Math.min(Math.max(plan.bufferPct, 0), 0.3);
  const buffer = subtotal * bufferPct;
  const annualTotal = subtotal + buffer;

  return {
    lines,
    byGroup,
    subtotal,
    buffer,
    annualTotal,
    monthlyTotal: annualTotal / 12,
    standardTotal: standardSubtotal * (1 + bufferPct),
  };
}

/** Things the calculator already handles, listed so nobody adds them twice. */
export const HANDLED_ELSEWHERE = [
  "School fees, transport and children's activities",
  "University, in India or abroad",
  "Health insurance for your household",
  "Care for dependent parents",
  "Travel to reach hospitals and services from a small town",
  "Tax on your withdrawals",
];
